import { isFulfilled } from '@reduxjs/toolkit';

import { buildSlice, CommonState, LoadingState } from '~libs/reduxUtils';

import { Supplier, SupplierInvoice, SupplierInvoiceStatus } from '~support-at-home/models';

import {
  getAll,
  getEstimate,
  getFile,
  getOne,
  typePrefix,
  updateStatus,
  uploadInvoiceFile,
} from '~support-at-home/actions/supplierInvoices';

interface State extends CommonState<SupplierInvoice> {
  suppliers: Supplier[];
  statuses: SupplierInvoiceStatus[];
  filesLoading: LoadingState;
  currentSupplierLoading: LoadingState;
  estimate?: any;
  total: number;
  count: number;
}

const initialState: State = {
  loading: 'idle',
  filesLoading: 'idle',
  currentSupplierLoading: 'idle',
  hasLoaded: false,
  error: null,
  rows: [],
  suppliers: [],
  statuses: [],
  total: 0,
  count: 0,
};

const slice = buildSlice<State, SupplierInvoice>(typePrefix, initialState, {
  extraReducers: (builder) => {
    builder.addCase(getAll.fulfilled, (state, action) => {
      state.loading = 'fulfilled';
      state.hasLoaded = true;
      state.rows = action.payload.data.rows ?? [];
      state.total = action.payload.data.total ?? 0;
      state.count = action.payload.data.count ?? 0;
    });
    builder.addCase(getOne.fulfilled, (state, action) => {
      state.row = action.payload.data;
      state.currentSupplierLoading = 'fulfilled';
    });
    builder.addCase(getEstimate.fulfilled, (state, action) => {
      state.loading = 'fulfilled';
      state.estimate = action.payload.data;
    });
    builder.addCase(uploadInvoiceFile.fulfilled, (state, action) => {
      const { id } = action.payload.data;

      if (!state.row) {
        state.row = {
          id: '',
          client_id: 0,
          supplier_id: '',
          created_at: '',
          reference: '',
          due_date: '',
          invoice_date: '',
          status_id: '',
          files: [
            {
              id,
              invoice_id: '',
              file_name: '',
              file_type: '',
              file_path: '',
            },
          ],
        };
      } else {
        state.row.files = [
          ...(state.row.files || []),
          {
            id,
            invoice_id: state.row.id,
            file_name: action.payload.data.file_name,
            file_type: action.payload.data.file_type,
            file_path: action.payload.data.file_path,
          },
        ];
      }
    });

    builder.addCase(getFile.fulfilled, (state, action) => {
      state.loading = 'fulfilled';

      if (!state.row) {
        return;
      }

      const fileId = action.meta.arg.id;
      const fileData = action.payload;

      const fileIndex = state.row.files?.findIndex((f) => f.id === fileId);
      if (fileIndex !== undefined && fileIndex >= 0 && state.row.files) {
        const updatedFile = {
          ...state.row.files[fileIndex],
          data: fileData,
        };
        state.row.files[fileIndex] = updatedFile;
      }
    });

    builder.addCase(getFile.rejected, (state) => {
      // Error handling is done in FileArea component
      // reduxUtils.setError filters file operations from global state
      state.loading = 'idle';
    });

    builder.addCase(updateStatus.fulfilled, (state, action) => {
      state.loading = 'fulfilled';

      const updated = action.payload.data;
      const index = state.rows.findIndex((row) => row.id === updated.id);

      if (index !== -1) {
        state.rows[index].status = updated.status;
        state.rows[index].status_id = updated.status_id;
      }
    });

    // Default matching for loading cases, pending when action is being called
    builder.addMatcher(isFulfilled, (state, { type }) => {
      if (type.startsWith(`${typePrefix}/`)) state.error = null;
    });
  },
  customActions: {
    clearCurrentInvoice(state) {
      URL.revokeObjectURL(state?.row?.file?.data ?? '');
      state.row = undefined;
      state.currentSupplierLoading = 'idle';
    },
    updateFile(state, action) {
      if (!state.row) return;

      const { data, targetFileId } = action.payload;
      if (!data || !targetFileId) return;

      const objectUrl = URL.createObjectURL(data);

      const fileIndex = state.row.files?.findIndex((f) => f.id === targetFileId);
      if (fileIndex !== undefined && fileIndex >= 0 && state.row.files) {
        URL.revokeObjectURL(state.row.files[fileIndex].data ?? '');

        const updatedFile = {
          ...state.row.files[fileIndex],
          file_name: data.name,
          file_type: data.type,
          file_path: objectUrl,
          data: objectUrl,
        };

        state.row.files[fileIndex] = updatedFile;
      }
    },
  },
});

export const { clear, clearCurrentInvoice, updateFile } = slice.actions;
export default slice.reducer;
